import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Skeleton
} from '@mui/material';
import { motion } from 'framer-motion';
import { Psychology, AutoAwesome, Security, Code } from '@mui/icons-material';
import { apiService } from '../services/api';
import PageSalesCta from '../components/PageSalesCta';

const About = () => {
  const theme = useTheme();
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAboutData();
      
      if (response.success) {
        setAboutData(response);
      } else {
        throw new Error('Failed to fetch about data');
      }
    } catch (err) {
      console.error('Error fetching about data:', err);
      setError('Failed to load page content. Please try again later.');
      // Set default data structure
      setAboutData({
        page: {
          page_title: 'About Boldcomp Solutions',
          hero_subtitle:
            'We are not a slide deck agency. We are a Nairobi team that ships software, AI, and automation so NGOs, SMEs, campaigns, and institutions save time, track what matters, and grow.',
          page_content: `
            <p>Boldcomp is based in Nairobi and works across Kenya and East Africa. We build custom systems — web, mobile, integrations, campaign tools — and we stay after launch so your staff are not left alone.</p>
            <p>If you are tired of Excel chaos, slow reporting, or tools that do not fit how you work, we start with your bottleneck and scope in plain language. Then we deliver in phases you can afford.</p>
          `
        },
        team: [],
        values: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getValueIcon = (iconName) => {
    const iconProps = { sx: { fontSize: 40, color: theme.palette.primary.main } };
    switch(iconName) {
      case 'innovation': return <AutoAwesome {...iconProps} />;
      case 'excellence': return <Psychology {...iconProps} />;
      case 'collaboration': return <Code {...iconProps} />;
      case 'integrity': return <Security {...iconProps} />;
      default: return <AutoAwesome {...iconProps} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Skeleton variant="text" height={80} width="60%" sx={{ mx: 'auto' }} />
          <Skeleton variant="text" height={40} width="80%" sx={{ mx: 'auto', my: 2 }} />
          <Grid container spacing={4}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!aboutData) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h4" color="error">
          Failed to load page content
        </Typography>
      </Box>
    );
  }

  const { page, team, values, about_config: aboutConfig } = aboutData;
  const whyChooseUs = Array.isArray(aboutConfig?.why_choose_us) && aboutConfig.why_choose_us.length > 0
    ? aboutConfig.why_choose_us
    : [
        { title: 'Local expertise', desc: 'Based in Nairobi. We understand Kenyan business needs, timelines, and budgets.' },
        { title: 'Outcome-focused', desc: 'We build solutions that deliver clear benefits: time saved, costs reduced, and growth enabled.' },
        { title: 'Practical and affordable', desc: 'No hype. We scope projects clearly, offer transparent pricing, and deliver what you need.' },
        { title: 'Long-term support', desc: 'We don\'t disappear after launch. Training, maintenance, and support are part of how we work.' },
      ];

  return (
    <Box sx={{ minHeight: '100vh', py: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            textAlign="center"
            sx={{
              color: theme.palette.primary.main,
              mb: 2,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            {page?.hero_title || page?.page_title || 'About Boldcomp Solutions'}
          </Typography>
          <Typography
            variant="h5"
            textAlign="center"
            sx={{
              mb: 6,
              color: 'text.secondary',
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            {page?.hero_subtitle ||
              'Nairobi team; work across Kenya and East Africa. Software, AI, automation, and campaign tools — with clear outcomes and support after launch.'}
          </Typography>
        </motion.div>

        {/* Main Content Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Box
                sx={{
                  '& p': { 
                    mb: 3, 
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                    color: 'text.primary'
                  },
                  '& strong': { 
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  }
                }}
              >
                {page?.page_content ? (
                  <div dangerouslySetInnerHTML={{ __html: page.page_content }} />
                ) : (
                  <>
                    <Typography variant="body1" paragraph>
                      <strong>Boldcomp Solutions</strong> is a technology company based in Nairobi. We work with Kenyan SMEs, NGOs, and institutions to deliver software and digital systems that are affordable, reliable, and built for local needs.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Our team combines experience in software development, design, and support. We focus on solving real business problems—reducing manual work, improving efficiency, and enabling growth—with long-term support you can depend on.
                    </Typography>
                  </>
                )}
              </Box>

              {/* Stats — outside rich-text box so parent & p { color } does not hit body2 <p> */}
              <Grid container spacing={3} sx={{ mt: 4 }}>
                {(Array.isArray(aboutConfig?.stats) && aboutConfig.stats.length > 0 ? aboutConfig.stats : [
                  { number: '50+', label: 'Projects Completed' },
                  { number: '30+', label: 'Happy Clients' },
                  { number: '5+', label: 'Years Experience' },
                  { number: '15+', label: 'Team Members' },
                ]).map((stat) => (
                  <Grid item xs={6} key={stat.label}>
                    <motion.div whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 300 }}>
                      <Card
                        elevation={0}
                        sx={{
                          textAlign: 'center',
                          p: 2,
                          bgcolor: 'primary.main',
                          color: 'common.white',
                          border: '1px solid rgba(255,255,255,0.12)',
                        }}
                      >
                        <CardContent sx={{ p: '8px !important', '&:last-child': { pb: '8px !important' } }}>
                          <Typography
                            component="div"
                            variant="h5"
                            sx={{ fontWeight: 800, mb: 0.5, color: 'common.white', lineHeight: 1.2 }}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            component="div"
                            variant="body2"
                            sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.92)', lineHeight: 1.35 }}
                          >
                            {stat.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  p: 4,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%'
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 3,
                    fontWeight: 600
                  }}
                >
                  Our Purpose
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ color: 'success.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.5rem' }}>🎯</span> Mission
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ pl: 3, lineHeight: 1.7 }}>
                    {aboutConfig?.mission || 'To give Kenyan businesses access to practical, affordable technology that saves time, cuts costs, and supports growth—with reliable delivery and ongoing local support.'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ color: 'info.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.5rem' }}>🔭</span> Vision
                  </Typography>
                  <Typography variant="body1" sx={{ pl: 3, lineHeight: 1.7 }}>
                    {aboutConfig?.vision || 'To be the trusted technology partner for SMEs, NGOs, and institutions across Kenya and East Africa—known for clarity, reliability, and commitment to long-term support.'}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Company Values Section */}
        {values && values.length > 0 && (
          <Box sx={{ mt: 8, mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Typography
                variant="h3"
                textAlign="center"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2,
                  fontWeight: 700
                }}
              >
                Our Values
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                sx={{
                  mb: 6,
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                What we stand for
              </Typography>

              <Grid container spacing={4}>
                {values.map((value, index) => (
                  <Grid item xs={12} sm={6} md={3} key={value.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <Card
                        sx={{
                          textAlign: 'center',
                          p: 4,
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ mb: 2 }}>
                            {getValueIcon(value.icon_name)}
                          </Box>
                          <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                            {value.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {value.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        )}

        {/* Why Choose Us Section */}
        <Box sx={{ mt: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <Typography variant="h3" textAlign="center" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
              Why Choose Us?
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {(whyChooseUs || [
                { 
                  title: 'Local expertise', 
                  desc: 'Based in Nairobi. We understand Kenyan business needs, timelines, and budgets—and we’re here for ongoing support.' 
                },
                { 
                  title: 'Outcome-focused', 
                  desc: 'We build solutions that deliver clear benefits: time saved, costs reduced, and growth enabled.' 
                },
                { 
                  title: 'Practical and affordable', 
                  desc: 'No hype. We scope projects clearly, offer transparent pricing, and deliver what you need to run your business better.' 
                },
                { 
                  title: 'Long-term support', 
                  desc: 'We don’t disappear after launch. Training, maintenance, and support are part of how we work.' 
                },
              ]).map((item, index) => (
                <Grid item xs={12} sm={6} key={item.title || index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.2 + (index * 0.1) }}
                  >
                    <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {item.desc}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>
      </Container>
      <PageSalesCta />
    </Box>
  );
};

export default About;